import React from 'react';
import { 
    Box, 
    Typography, 
    Card, 
    CardContent,
    useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledStatCard = styled(Card)(({ theme }) => ({
    width: '140px', // Fixed width
    height: '140px', // Fixed height
    minWidth: '140px', // Prevent shrinking
    border: `1px solid ${theme.palette.grey[500]}`,
    borderRadius: theme.spacing(2), // 16px border radius to match other cards
    transition: 'all 0.2s ease-in-out',
}));

const IconContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center', // Center vertically
    justifyContent: 'center',
    width: 32, // 32px container
    height: 32, // 32px container
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    position: 'absolute',
    top: theme.spacing(1.5), // 12px from top
    left: theme.spacing(1.5), // 12px from left
}));

const ContentContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center', // Center content vertically
    height: '100%',
    paddingTop: '48px', // Space for icon (32px + 16px margin)
    paddingLeft: '12px',
    paddingRight: '12px',
    paddingBottom: '12px',
}));

/**
 * StatCard Component
 * 
 * Displays a single statistic with icon, value, and optional limit.
 * Used for showing user dashboard statistics like templates used, posts scheduled, etc.
 * Compressed to 140px × 140px for the KPI deck layout.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Icon component to display
 * @param {string} props.title - Title/label for the statistic
 * @param {number} props.value - Current value/count
 * @param {number|null} props.limit - Optional limit value
 * @param {string} props.subtitle - Optional subtitle for additional context
 * @param {string} props.color - Optional color theme ('primary', 'secondary', 'success', etc.)
 */
const StatCard = ({ 
    icon, 
    title, 
    value, 
    limit, 
    color = 'primary', 
    loading = false 
}) => {
    const theme = useTheme();
    
    // Calculate if we're at 80% of limit for color change
    const isNearLimit = limit && value && (value / limit) >= 0.8;
    const displayValue = loading ? '...' : (value || 0);
    
    // Get color based on prop - restored original logic
    const getColor = () => {
        switch (color) {
            case 'secondary':
                return theme.palette.secondary.main;
            case 'success':
                return theme.palette.success.main;
            case 'warning':
                return theme.palette.warning.main;
            case 'error':
                return theme.palette.error.main;
            default:
                return theme.palette.primary.main;
        }
    };
    
    return (
        <StyledStatCard>
            <CardContent sx={{ 
                position: 'relative', 
                height: '100%', 
                p: 0,
                '&:last-child': { pb: 0 }
            }}>
                {/* Icon container positioned top-left */}
                <IconContainer sx={{ 
                    backgroundColor: `${getColor()}15`, // Restored original background
                    color: getColor() // Restored original color
                }}>
                    {React.cloneElement(icon, {
                        sx: { 
                            fontSize: 20, // Slightly smaller icon
                            display: 'flex',
                            alignItems: 'center', // Ensure icon itself is centered
                        }
                    })}
                </IconContainer>
                
                {/* Content container with proper vertical centering */}
                <ContentContainer>
                    <Typography 
                        variant="h4" 
                        component="div" 
                        sx={{ 
                            fontSize: '2rem', // 32px
                            lineHeight: '2.25rem', // 36px
                            fontWeight: 'bold',
                            color: isNearLimit ? 'warning.main' : 'text.primary',
                            mb: 0.5,
                            display: 'flex',
                            alignItems: 'center', // Center the number vertically
                            minHeight: '36px' // Ensure consistent height
                        }}
                    >
                        {displayValue}
                        {limit && (
                            <Typography 
                                component="span" 
                                variant="caption" 
                                sx={{ 
                                    ml: 0.5, 
                                    color: 'text.secondary',
                                    fontSize: '0.75rem',
                                    alignSelf: 'flex-end',
                                    lineHeight: 1
                                }}
                            >
                                /{limit}
                            </Typography>
                        )}
                    </Typography>
                    
                    <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                            fontSize: '0.75rem', // 12px
                            lineHeight: '1.125rem', // 18px
                            fontWeight: 500,
                            textAlign: 'left',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}
                    >
                        {title}
                    </Typography>
                </ContentContainer>
            </CardContent>
        </StyledStatCard>
    );
};

export default StatCard; 